<template>

    <div class="row">
        <div class="col-md-6 col-lg-4">
            <div class="box box-solid">
                <div class="box-header">
                    <h2 class="box-title">{{ Lang.get("contacts.show.contactdata-header") }}</h2>
                    <div class="box-tools">
                        <router-link
                            v-if="canEditContact"
                            class="btn btn-primary btn-xs"
                            :to="{name:'crm.contacts.edit', params:{uuid:contact.uuid}}"
                            v-tooltip="'Contact bewerken'"
                        >
                            <i class="mdi mdi-pencil"></i>
                        </router-link>

                        <delete-button
                            v-if="canDeleteContact"
                            :url="route('api.v1.crm.contacts.destroy', {uuid:contact.uuid})"
                            :name="contact.fullname"
                            v-tooltip="'Contact verwijderen'"
                            title="Contactpersoon"
                            classes="btn btn-danger btn-xs"
                            :disabled="!contact.candelete"
                        ></delete-button>
                    </div>
                </div>
                <div class="box-body no-padding">
                    <table class="table table-striped">
                        <tbody>
                            <tr>
                                <th class="col-lg-4">{{ Lang.get("global.fullname") }}</th>
                                <td>{{ contact.fullname }}</td>
                            </tr>
                            <tr>
                                <th>{{ Lang.get("global.title") }}</th>
                                <td>{{ contact.title }}</td>
                            </tr>
                            <tr>
                                <th>{{ Lang.get("global.firstname") }}</th>
                                <td>{{ contact.firstname }}</td>
                            </tr>
                            <tr>
                                <th>{{ Lang.get("global.middlename") }}</th>
                                <td>{{ contact.middlename }}</td>
                            </tr>
                            <tr>
                                <th>{{ Lang.get("global.lastname") }}</th>
                                <td>{{ contact.lastname }}</td>
                            </tr>
                            <tr>
                                <th>{{ Lang.get("global.mobile") }}</th>
                                <td>{{ contact.mobile }}</td>
                            </tr>
                            <tr>
                                <th>{{ Lang.get("global.phone") }}</th>
                                <td>{{ contact.phone }}</td>
                            </tr>
                            <tr>
                                <th>{{ Lang.get("global.phone") }} (alt)</th>
                                <td>{{ contact.phone_alt }}</td>
                            </tr>
                            <tr>
                                <th>{{ Lang.get("global.fax") }}</th>
                                <td>{{ contact.fax }}</td>
                            </tr>
                            <tr>
                                <th>{{ Lang.get("global.email") }}</th>
                                <td>{{ contact.email }}</td>
                            </tr>
                            <tr>
                                <th>{{ Lang.get("global.email") }} (alt)</th>
                                <td>{{ contact.email_alt }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
            <div class="box box-solid">
                <div class="box-header">
                    <h2 class="box-title">{{ Lang.get("contacts.show.addressdata-header") }}</h2>
                </div>
                <div class="box-body no-padding">
                    <table class="table table-striped">
                        <tbody>
                            <tr>
                                <th class="col-lg-4">{{ Lang.get("global.address") }}</th>
                                <td>{{ contact.address }}</td>
                            </tr>
                            <tr>
                                <th>{{ Lang.get("global.zipcode") }}</th>
                                <td>{{ contact.zipcode }}</td>
                            </tr>
                            <tr>
                                <th>{{ Lang.get("global.city") }}</th>
                                <td>{{ contact.city }}</td>
                            </tr>
                            <tr>
                                <th>{{ Lang.get("global.country") }}</th>
                                <td>{{ contact.country }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
        <div class="col-md-6 col-lg-4">
            <div class="box box-solid">
                <div class="box-header">
                    <h2 class="box-title">{{ Lang.get("contacts.show.connectedcrmgroups") }}</h2>
                </div>
                <div class="box-body no-padding">
                    <table class="table table-striped">
                        <thead>
                            <tr>
                                <th>Groep</th>
                                <th>&nbsp;</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-if="contact.crmgroups.length === 0"><td colspan="2">Dit contact is niet aan een groep gekoppeld</td></tr>
                            <tr
                                v-for="group in contact.crmgroups"
                                :id="'row-' + group.uuid"
                            >
                                <td>
                                    <router-link
                                        :to="{name:'crm.groups.show', params:{uuid:group.uuid}}"
                                    >
                                        {{ group.name }}
                                    </router-link>
                                </td>
                                <td class="text-right">
                                    <delete-button
                                        :parent-element="'row-' + group.uuid"
                                        :url="route('api.v1.crm.groups.detach', {group_uuid:group.uuid, target:'crmgroup', target_uuid:contact.uuid})"
                                        title="Gekoppelde groep"
                                        :name="group.name"
                                        class="btn-xs"
                                    ></delete-button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
            <div
                class="box box-solid"
                v-if="can('read','companies')"
            >
                <div class="box-header">
                    <h2 class="box-title">{{ Lang.get("contacts.show.connectedcompanies") }}</h2>
                    <div
                        v-if="canUpdateContact"
                        class="box-tools"
                    >
                        <connections
                            orig="contact"
                            :orig-id="getContact.uuid"
                            target="company"
                            :attach-url="route('api.v1.crm.contacts.attach', {uuid:getContact.uuid}, false)"
                            event-on-completed="connectionCompleted"
                            event-on-failed="connectionFailed"
                            @connectionCompleted="companyConnectionCompleted"
                            @connectionFailed="companyConnectionFailed"
                        ></connections>
                    </div>
                </div>
                <div
                    class="box-body no-padding"
                >
                    <table class="table table-striped">
                        <thead>
                            <tr>
                                <th>{{ Lang.get("global.company") }}</th>
                                <th>{{ Lang.get("contacts.show.function") }}</th>
                                <th>&nbsp;</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-if="contact.companies.length === 0 && contact.oldcompanies.length === 0">
                                <td colspan="3">{{ Lang.get("companies.show.noconnectedcompanies") }}</td>
                            </tr>

                            <tr
                                v-for="company in contact.companies"
                            >
                                <td>
                                    <router-link
                                        :to="{name:'crm.companies.show', params:{uuid:company.uuid}}"
                                    >
                                        {{ company.name }}
                                    </router-link>
                                </td>
                                <td>
                                    <company-function
                                        v-if="canUpdateContact"
                                        :current="company.pivot.function"
                                        :url="route('api.v1.crm.updatefunction', {contact_uuid: contact.uuid, company_uuid: company.uuid})"
                                    ></company-function>
                                    <span v-else>
                                        {{ company.pivot.function }}
                                    </span>
                                </td>
                                <td class="text-right">
                                    <delete-button
                                        v-if="canUpdateContact"
                                        title="Koppeling met bedrijf"
                                        class="btn-xs"
                                        v-tooltip="'Bedrijf loskoppelen'"
                                        :url="route('api.v1.crm.companies.detach', {uuid:company.uuid, target:'contact', target_uuid: contact.uuid})"
                                        :name="'koppeling met bedrijf: ' + company.name"
                                        :show-snotify="false"
                                        @delete-completed="companyDetachCompleted"
                                    ></delete-button>
                                </td>
                            </tr>

                            <tr
                                v-for="company in contact.oldcompanies"
                                class="strikethrough"
                            >
                                <td>
                                    <router-link
                                        :to="{name:'crm.companies.show', params:{uuid:company.uuid}}"
                                    >
                                        {{ company.name }}
                                    </router-link>
                                </td>
                                <td>{{ company.pivot.function }}</td>
                                <td class="text-right">
                                    <reconnect
                                        v-if="canUpdateContact"
                                        target="contacts"
                                        type="Contactpersoon"
                                        :uuid="contact.uuid"
                                        :target-uuid="company.uuid"
                                        @reconnectCompleted="reconnectCompleted"
                                        @reconnectFailed="reconnectFailed"
                                    ></reconnect>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
            <div
                class="box box-solid"
                v-if="can('read','projects')"
            >
                <div class="box-header">
                    <h2 class="box-title">Gekoppelde projecten</h2>
                    <div
                        v-if="canUpdateContact"
                        class="box-tools"
                    >
                        <connections
                            orig="contact"
                            :orig-id="getContact.uuid"
                            target="project"
                            :attach-url="route('api.v1.crm.contacts.attach', {uuid:getContact.uuid}, false)"
                            event-on-completed="connectionCompleted"
                            event-on-failed="connectionFailed"
                            @connectionCompleted="projectConnectionCompleted"
                            @connectionFailed="projectConnectionFailed"
                        ></connections>
                    </div>
                </div>
                <div class="box-body no-padding">
                    <table class="table table-striped">
                        <thead>
                            <tr>
                                <th>{{ Lang.get("global.project") }}</th>
                                <th>{{ Lang.get("global.name") }}</th>
                                <th>&nbsp;</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-if="contact.projects.length === 0"><td colspan="3">Er zijn geen gekoppelde projecten beschikbaar</td></tr>
                            <tr
                                v-for="project in contact.projects"
                            >
                                <td>
                                    <router-link
                                        :to="{name:'projects.details.index', params:{uuid: project.uuid}}"
                                    >
                                        {{ project.project_name }}
                                    </router-link>
                                </td>
                                <td>{{ project.name }}</td>
                                <td class="text-right">
                                    <delete-button
                                        v-if="canUpdateContact"
                                        :url="route('api.v1.crm.contacts.detach', {uuid:contact.uuid, target:'project', target_uuid: project.uuid})"
                                        title="Koppeling met project"
                                        :name="'koppeling met project: ' + project.project_name"
                                        class="btn-xs"
                                        v-tooltip="'Project loskoppelen'"
                                        :show-snotify="false"
                                        @delete-completed="projectDetachCompleted"
                                    ></delete-button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
        <div class="col-md-6 col-lg-4">
            <div class="box box-solid">
                <div class="box-header">
                    <h2 class="box-title">{{ Lang.get("notes.show.header") }}</h2>
                </div>
                <div class="box-body" v-if="contact.notes.length === 0">
                    <p>{{ Lang.get("notes.show.nonotes") }}</p>
                </div>
                <div class="box-body no-padding" v-else>
                    <table class="table table-striped">
                        <tbody class="crmNotesList">
                            <tr
                                v-for="note in contact.notes"
                                :id="'contactnote-' + note.uuid"
                            >
                                <td colspan="3">
                                    <div>
                                        <p>
                                            <strong>Door:</strong>
                                            <strike
                                                v-if="!note.creator.active || note.creator.deleted_at !== null"
                                                v-tooltip="'Deze medewerker werkt hier niet meer'"
                                            >
                                                {{ note.creator.fullname }}
                                            </strike>
                                            <span v-else>{{ note.creator.fullname }}</span>
                                        </p>
                                        <p>
                                            <strong>Datum:</strong>
                                            {{ note.created_at | formatDate }}
                                        </p>
                                        <p><strong>Notitie:</strong></p>
                                        <div v-html="note.note"></div>
                                        <div class="text-right">
                                            <delete-button
                                                v-if="canDeleteNote"
                                                :url="route('api.v1.crm.contacts.notes.delete', {uuid:note.uuid})"
                                                :title="Lang.get('notes.show.note')"
                                                :name="Lang.get('notes.show.noteby') + note.creator.fullname"
                                                :parent-element="'contactnote-' + note.uuid"
                                                v-tooltip="'Notitie verwijderen'"
                                            ></delete-button>
                                        </div>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
            <add-note
                v-if="canAddNote"
                :post-url="route('api.v1.crm.contacts.notes.add', {contact_uuid: contact.uuid})"
                :user-name="getAuth.fullname"
                @noteAdded="$emit('contactDataChanged')"
            ></add-note>
        </div>
    </div>

</template>

<script>

import { mapGetters } from 'vuex';
import Lang from '../../../Lang';
import AddNote from "../../../components/CRM/AddNote";
import CompanyFunction from "../../../components/CRM/CompanyFunction";
import Reconnect from "../../../components/CRM/Reconnect";
import Connections from "../../../components/Common/Connections";

export default {

    components: {
        AddNote,
        CompanyFunction,
        Reconnect,
        Connections,
    },

    computed: {
        ...mapGetters([
            'can',
            'getAuth',
            'getContact',
        ]),

        canAddNote() {
            return this.can('write', 'contacts');
        },

        canDeleteContact() {
            return this.can('write', 'contacts');
        },

        canDeleteNote() {
            return this.can('write', 'contacts');
        },

        canEditContact() {
            return this.can('write', 'contacts');
        },

        canUpdateContact() {
            return this.can('write', 'contacts');
        },

        contact() {
            return this.getContact;
        },
    },

    data() {
        return {
            Lang:Lang,
        }
    },

    metaInfo: {
        title:'Contact',
    },

    methods: {
        companyConnectionCompleted() {
            this.$emit('contactDataChanged');
            this.$snotify.success('Het bedrijf is gekoppeld');
        },
        companyConnectionFailed() {
            this.$snotify.error('Er is een fout opgetreden bij het koppelen van het bedrijf');
        },
        companyDetachCompleted() {
            this.$emit('contactDataChanged');
            this.$snotify.success('Het bedrijf is ontkoppeld');
        },
        projectConnectionCompleted() {
            this.$emit('contactDataChanged');
            this.$snotify.success('Het project is gekoppeld');
        },
        projectConnectionFailed() {
            this.$snotify.error('Er is een fout opgetreden bij het koppelen van het project');
        },
        projectDetachCompleted() {
            this.$emit('contactDataChanged');
            this.$snotify.success('Het project is ontkoppeld');
        },
        reconnectCompleted() {
            this.$snotify.success('Opnieuw verbinden is gelukt');
            this.$emit('contactDataChanged');
        },
        reconnectFailed() {
            this.$snotify.error('Er is een fout opgetreden bij het opnieuw verbinden');
        },
    },

    name:'CrmContactsShow',

}

</script>
